import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNotificationContext } from '../../contexts/NotificationContext'

// Walidacja plików po stronie klienta (zanim cokolwiek poleci na serwer)
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif']
const MAX_FILE_SIZE_MB = 10
const MAX_FILES_PER_UPLOAD = 20

function splitValidImageFiles(files: File[]): { valid: File[]; rejected: string[] } {
  const valid: File[] = []
  const rejected: string[] = []
  for (const f of files) {
    const typeOk = ALLOWED_IMAGE_TYPES.includes(f.type) || /\.(jpe?g|png|webp|heic|heif)$/i.test(f.name)
    if (!typeOk) {
      rejected.push(`${f.name} — niedozwolony format (dozwolone: JPG, PNG, WebP, HEIC)`)
      continue
    }
    if (f.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      rejected.push(`${f.name} — za duży plik (${(f.size / 1024 / 1024).toFixed(1)} MB, limit ${MAX_FILE_SIZE_MB} MB)`)
      continue
    }
    valid.push(f)
  }
  if (valid.length > MAX_FILES_PER_UPLOAD) {
    const overflow = valid.splice(MAX_FILES_PER_UPLOAD)
    for (const f of overflow) rejected.push(`${f.name} — przekroczono limit ${MAX_FILES_PER_UPLOAD} plików na raz`)
  }
  return { valid, rejected }
}

// Custom hook for mobile detection
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }

    checkIsMobile()
    window.addEventListener('resize', checkIsMobile)
    return () => window.removeEventListener('resize', checkIsMobile)
  }, [])

  return isMobile
}

// Custom hook for dynamic modal height on mobile
const useModalHeight = (isMobile: boolean) => {
  const [modalHeight, setModalHeight] = useState('90vh')

  useEffect(() => {
    if (!isMobile) {
      setModalHeight('90vh')
      return
    }

    const updateHeight = () => {
      const vh = window.innerHeight * 0.01
      document.documentElement.style.setProperty('--vh', `${vh}px`)
      // Dla starszych urządzeń mobilnych zmniejszamy wysokość modalu
      const newHeight = Math.min(window.innerHeight - 40, window.innerHeight * 0.85)
      setModalHeight(`${newHeight}px`)
    }

    updateHeight()
    window.addEventListener('resize', updateHeight)
    window.addEventListener('orientationchange', updateHeight)

    return () => {
      window.removeEventListener('resize', updateHeight)
      window.removeEventListener('orientationchange', updateHeight)
    }
  }, [isMobile])

  return modalHeight
}

type WorkType = 'konserwacja' | 'awaria' | ''
type ReportedBy = 'system' | 'osoba' | ''
type ReportCategory = 'water' | 'sewer' | ''

interface Client { id: number; name: string }

interface ReportData {
  client_id: number | null
  work_type: WorkType
  category: ReportCategory
  reported_by: ReportedBy
  reporter_name: string
  reporter_phone: string
  description: string
  is_finished: boolean
  materials: string
  recommendations: string
  location: string
  street_address: string
  latitude: string
  longitude: string
  relates_to_infrastructure: boolean
  infrastructure_object_id: number | null
  gpsSelected: boolean
}

interface InfrastructureObject {
  id: number
  name: string
  address: string
  location: string
  street_address: string
  latitude: string
  longitude: string
}

interface ReportImage {
  id: number
  filename: string
  original_filename: string
  file_path: string
  file_size: number
  mime_type: string
  uploaded_at?: string | null
}

interface ReportCreatorProps {
  token: string
  isOpen: boolean
  onClose: () => void
  onReportCreated: () => void
  reportId?: string // tryb edycji jeśli podane
}

// Trwały szkic zapisywany TYLKO po kliknięciu "Zapisz wersję roboczą"
const DRAFT_KEY = 'report_draft_v2'

export default function ReportCreator({ token, isOpen, onClose, onReportCreated, reportId }: ReportCreatorProps) {
  const { showError, showSuccess, showConfirm } = useNotificationContext()
  const isMobile = useIsMobile()
  const modalHeight = useModalHeight(isMobile)

  const [step, setStep] = useState<number>(1)
  const [clients, setClients] = useState<Client[]>([])
  const [loadingClients, setLoadingClients] = useState<boolean>(true)
  const [infrastructureObjects, setInfrastructureObjects] = useState<InfrastructureObject[]>([])
  const [loadingInfrastructure, setLoadingInfrastructure] = useState<boolean>(false)
  const [saving, setSaving] = useState<boolean>(false)
  const [currentReportId, setCurrentReportId] = useState<string>('') // w edycji/podglądzie
  const [images, setImages] = useState<ReportImage[]>([]) // tylko edycja
  const [filesToUpload, setFilesToUpload] = useState<File[]>([]) // ostatni krok kreatora lub edycja
  const [wantImages, setWantImages] = useState<'yes' | 'no'>('no') // decyzja w kreatorze

  const emptyData: ReportData = useMemo(() => ({
    client_id: null,
    work_type: '',
    category: '',
    reported_by: '',
    reporter_name: '',
    reporter_phone: '',
    description: '',
    is_finished: false,
    materials: '',
    recommendations: '',
    location: '',
    street_address: '',
    latitude: '',
    longitude: '',
    relates_to_infrastructure: false,
    infrastructure_object_id: null,
    gpsSelected: false
  }), [])

  const [form, setForm] = useState<ReportData>(emptyData)
  const isEditMode = Boolean(reportId)

  // Migawka stanu początkowego formularza - do wykrywania niezapisanych zmian
  // przy zamykaniu (ochrona przed przypadkową utratą danych).
  const initialFormRef = useRef<string>(JSON.stringify(emptyData))

  // Załaduj klientów i ew. dane raportu / zapisany szkic (ręcznie zapisany wcześniej)
  useEffect(() => {
    if (!isOpen) return
    loadClients()

    if (isEditMode && reportId) {
      void loadReport(reportId)
    } else {
      // NIE ma auto-cache. Czytamy TYLKO zapisany wcześniej szkic (trwale, na żądanie użytkownika).
      const cached = localStorage.getItem(DRAFT_KEY)
      if (cached) {
        try {
          const parsed = JSON.parse(cached) as ReportData
          const restored = { ...emptyData, ...parsed }
          setForm(restored)
          initialFormRef.current = JSON.stringify(restored)
        } catch {
          setForm(emptyData)
          initialFormRef.current = JSON.stringify(emptyData)
        }
      } else {
        setForm(emptyData)
        initialFormRef.current = JSON.stringify(emptyData)
      }
      setStep(1)
      setCurrentReportId('')
      setFilesToUpload([])
      setWantImages('no')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, reportId])

  // >>> UWAGA: brak useEffect z auto-zapisywaniem do localStorage! <<<
  // Stan "tymczasowy" istnieje tylko w pamięci na czas otwartego okna modala.

  const resetEphemeralState = () => {
    // czyści wyłącznie pamięć operacyjną (tymczasowy cache kroków)
    setForm(emptyData)
    setStep(1)
    setWantImages('no')
    setFilesToUpload([])
    setImages([])
    setCurrentReportId('')
  }

  const loadClients = async () => {
    try {
      setLoadingClients(true)
      const resp = await fetch('/api/clients/list', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!resp.ok) throw new Error()
      const data: Client[] = await resp.json()
      setClients(data)
      if (!isEditMode && data.length > 0 && !form.client_id) {
        setForm(f => {
          const next = { ...f, client_id: data[0].id }
          // Auto-uzupełnienie domyślnego klienta nie jest zmianą użytkownika -
          // aktualizujemy migawkę, by nie wywoływać fałszywego ostrzeżenia przy zamykaniu
          if (JSON.stringify(f) === initialFormRef.current) {
            initialFormRef.current = JSON.stringify(next)
          }
          return next
        })
      }
    } catch {
      showError('Błąd ładowania', 'Nie można pobrać listy klientów')
    } finally {
      setLoadingClients(false)
    }
  }

  const loadInfrastructureObjects = async (clientId: number) => {
    try {
      setLoadingInfrastructure(true)
      const resp = await fetch(`/api/infrastructure/client/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!resp.ok) throw new Error()
      const data: InfrastructureObject[] = await resp.json()
      setInfrastructureObjects(data)
    } catch {
      showError('Błąd ładowania', 'Nie można pobrać listy obiektów infrastruktury')
      setInfrastructureObjects([])
    } finally {
      setLoadingInfrastructure(false)
    }
  }

  const loadReport = async (id: string) => {
    try {
      const resp = await fetch(`/api/reports/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!resp.ok) throw new Error()
      const data = await resp.json()
      setCurrentReportId(data.id)
      const merged: ReportData = {
        client_id: data.client_id ?? null,
        work_type: (data.work_type ?? '') as WorkType,
        category: (data.category ?? '') as ReportCategory,
        reported_by: (data.reported_by ?? '') as ReportedBy,
        reporter_name: data.reporter_name ?? '',
        reporter_phone: data.reporter_phone ?? '',
        description: data.description ?? '',
        is_finished: Boolean(data.is_finished),
        materials: data.materials ?? '',
        recommendations: data.recommendations ?? '',
        location: data.location ?? '',
        street_address: data.street_address ?? '',
        latitude: data.latitude ?? '',
        longitude: data.longitude ?? '',
        relates_to_infrastructure: Boolean(data.relates_to_infrastructure),
        infrastructure_object_id: data.infrastructure_object_id ?? null,
        gpsSelected: false
      }
      setForm(merged)
      initialFormRef.current = JSON.stringify(merged)
      setImages(Array.isArray(data.images) ? data.images : [])
      setFilesToUpload([])
    } catch {
      showError('Błąd', 'Nie udało się pobrać danych raportu do edycji')
      onClose()
    }
  }

  const validate = (): string | null => {
    if (!form.client_id) return 'Wybierz klienta.'
    if (form.work_type !== 'konserwacja' && form.work_type !== 'awaria') return 'Wybierz rodzaj pracy.'
    if (form.category !== 'water' && form.category !== 'sewer') return 'Wybierz kategorię raportu: wodociągi lub kanalizacja.'
    if (form.work_type === 'awaria') {
      if (form.reported_by !== 'system' && form.reported_by !== 'osoba') {
        return 'Dla awarii wskaż, kto zgłosił: system lub osoba.'
      }
    }

    // Sprawdź czy adres jest wymagany - tylko jeśli nie zostały podane współrzędne GPS i nie wybrano GPS
    const hasGPS = form.latitude && form.longitude && form.latitude.trim() && form.longitude.trim()
    if (!hasGPS && !form.gpsSelected && !form.relates_to_infrastructure) {
      // Jeśli nie ma GPS, nie wybrano GPS i nie dotyczy infrastruktury, sprawdź adres
      if (!form.street_address || !form.street_address.trim()) {
        return 'Pole \'Ulica/nr działki\' jest wymagane i nie może być puste (chyba że zostały podane współrzędne GPS lub wybrano opcję GPS).'
      }
    }

    if (form.recommendations && form.recommendations.length > 5000) {
      return 'Pole „Zalecenia” przekracza 5000 znaków.'
    }
    return null
  }

  // Trwały zapis szkicu — tylko na wyraźne kliknięcie
  const handleSaveDraftClick = () => {
    if (isEditMode) {
      showError('Info', 'Wersja robocza (localStorage) dostępna tylko przy tworzeniu nowego raportu.')
      return
    }
    localStorage.setItem(DRAFT_KEY, JSON.stringify(form))
    showSuccess('Zapisano', 'Wersja robocza zapisana lokalnie w przeglądarce.')
  }

  const clearDraft = async () => {
    if (isEditMode) return
    const ok = await showConfirm('Wyczyścić wersję roboczą?', 'Operacja usunie lokalny szkic.')
    if (!ok) return
    localStorage.removeItem(DRAFT_KEY)
    showSuccess('Wyczyszczono', 'Usunięto wersję roboczą.')
  }

  const hasUnsavedChanges = () =>
    JSON.stringify(form) !== initialFormRef.current || filesToUpload.length > 0

  const handleClose = async () => {
    // Ochrona przed przypadkową utratą danych: pytamy tylko, gdy użytkownik
    // faktycznie coś zmienił (lub wybrał zdjęcia do wgrania).
    if (hasUnsavedChanges()) {
      const ok = await showConfirm(
        'Niezapisane zmiany',
        isEditMode
          ? 'Masz niezapisane zmiany w raporcie. Zamknąć bez zapisywania?'
          : 'Wprowadzone dane nie zostały zapisane. Możesz najpierw kliknąć „Zapisz wersję roboczą”. Zamknąć bez zapisu?'
      )
      if (!ok) return
    }
    // Porzucenie procesu: zerujemy TYLKO stan tymczasowy (brak ingerencji w localStorage)
    resetEphemeralState()
    onClose()
  }

  // Zamykanie klawiszem Escape (z tą samą ochroną przed utratą danych)
  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') void handleClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, form, filesToUpload, isEditMode])

  // Wybór plików z walidacją po stronie klienta (format, rozmiar, liczba)
  const handleFilesSelected = (fileList: FileList | null) => {
    const files = fileList ? Array.from(fileList) : []
    const { valid, rejected } = splitValidImageFiles(files)
    if (rejected.length) {
      showError('Część plików odrzucono', rejected.join('\n'))
    }
    setFilesToUpload(valid)
  }

  // Upload obrazów (wykorzystywane w ostatnim kroku kreatora oraz w edycji)
  const uploadImages = async (id: string, files: File[]) => {
    if (!files.length) return
    const formData = new FormData()
    for (const f of files) formData.append('files', f)
    const resp = await fetch(`/api/reports/${id}/images`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData
    })
    if (!resp.ok) {
      let msg = 'Nie udało się wgrać zdjęć.'
      try {
        const d = await resp.json()
        if (d.detail) {
          if (Array.isArray(d.detail)) {
            // Obsługa błędów walidacji Pydantic (tablica obiektów)
            msg = d.detail.map((err: any) => {
              if (typeof err === 'string') return err
              return err.msg || err.message || 'Błąd walidacji'
            }).join('; ')
          } else if (typeof d.detail === 'string') {
            msg = d.detail
          } else if (typeof d.detail === 'object' && d.detail.msg) {
            msg = d.detail.msg
          }
        }
      } catch {}
      throw new Error(msg)
    }
    const uploaded = await resp.json()
    return uploaded as ReportImage[]
  }

  const deleteImage = async (imageId: number) => {
    if (!currentReportId) return
    const ok = await showConfirm('Usunąć zdjęcie?', 'Plik zostanie trwale usunięty.')
    if (!ok) return
    const resp = await fetch(`/api/reports/${currentReportId}/images/${imageId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` }
    })
    if (!resp.ok) {
      showError('Błąd', 'Nie udało się usunąć zdjęcia.')
      return
    }
    setImages(imgs => imgs.filter(i => i.id !== imageId))
    showSuccess('Usunięto', 'Zdjęcie zostało usunięte.')
  }

  // Zapis całości (kreator)
  const submitCreate = async () => {
    const err = validate()
    if (err) {
      showError('Błąd walidacji', err)
      return
    }
    setSaving(true)
    try {
      // 1) utwórz raport
      const resp = await fetch('/api/reports', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: form.client_id,
          work_type: form.work_type,
          category: form.category,
          reported_by: form.work_type === 'awaria' ? (form.reported_by || null) : null,
          reporter_name: form.work_type === 'awaria' && form.reported_by === 'osoba' ? nullIfEmpty(form.reporter_name) : null,
          reporter_phone: form.work_type === 'awaria' && form.reported_by === 'osoba' ? nullIfEmpty(form.reporter_phone) : null,
          description: nullIfEmpty(form.description),
          is_finished: Boolean(form.is_finished),
          materials: nullIfEmpty(form.materials),
          recommendations: nullIfEmpty(form.recommendations),
          location: nullIfEmpty(form.location),
          street_address: nullIfEmpty(form.street_address),
          latitude: nullIfEmpty(form.latitude),
          longitude: nullIfEmpty(form.longitude),
          infrastructure_object_id: form.relates_to_infrastructure ? form.infrastructure_object_id : null,
        }),
      })
      if (!resp.ok) {
        let msg = 'Nie udało się zapisać raportu.'
        try {
          const d = await resp.json()
          if (d.detail) {
            if (Array.isArray(d.detail)) {
              // Obsługa błędów walidacji Pydantic (tablica obiektów)
              msg = d.detail.map((err: any) => {
                if (typeof err === 'string') return err
                return err.msg || err.message || 'Błąd walidacji'
              }).join('; ')
            } else if (typeof d.detail === 'string') {
              msg = d.detail
            } else if (typeof d.detail === 'object' && d.detail.msg) {
              msg = d.detail.msg
            }
          }
        } catch {}
        showError('Błąd', msg)
        return
      }
      const data = await resp.json()
      const newId: string = data.id

      // 2) jeśli użytkownik chce dodać zdjęcia — wyślij
      if (wantImages === 'yes' && filesToUpload.length) {
        await uploadImages(newId, filesToUpload)
      }

      // 3) sukces
      localStorage.removeItem(DRAFT_KEY) // gotowy raport — czyścimy ewentualny szkic
      showSuccess('Sukces', `Raport utworzony ${wantImages === 'yes' ? 'i zdjęcia zapisane' : ''}.`)
      resetEphemeralState()
      onReportCreated()
      onClose()
    } catch {
      showError('Błąd połączenia', 'Nie można połączyć się z serwerem.')
    } finally {
      setSaving(false)
    }
  }

  // Zapis w edycji (formularz zbiorczy)
  const submitEdit = async () => {
    const err = validate()
    if (err) {
      showError('Błąd walidacji', err)
      return
    }
    if (!reportId) return
    setSaving(true)
    try {
      const resp = await fetch(`/api/reports/${reportId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: form.client_id,
          work_type: form.work_type,
          category: form.category,
          reported_by: form.work_type === 'awaria' ? (form.reported_by || null) : null,
          reporter_name: form.work_type === 'awaria' && form.reported_by === 'osoba' ? nullIfEmpty(form.reporter_name) : null,
          reporter_phone: form.work_type === 'awaria' && form.reported_by === 'osoba' ? nullIfEmpty(form.reporter_phone) : null,
          description: nullIfEmpty(form.description),
          is_finished: Boolean(form.is_finished),
          materials: nullIfEmpty(form.materials),
          recommendations: nullIfEmpty(form.recommendations),
          location: nullIfEmpty(form.location),
          street_address: nullIfEmpty(form.street_address),
          latitude: nullIfEmpty(form.latitude),
          longitude: nullIfEmpty(form.longitude),
          infrastructure_object_id: form.relates_to_infrastructure ? form.infrastructure_object_id : null,
        }),
      })
      if (!resp.ok) {
        let msg = 'Nie udało się zaktualizować raportu.'
        try {
          const d = await resp.json()
          if (d.detail) {
            if (Array.isArray(d.detail)) {
              // Obsługa błędów walidacji Pydantic (tablica obiektów)
              msg = d.detail.map((err: any) => {
                if (typeof err === 'string') return err
                return err.msg || err.message || 'Błąd walidacji'
              }).join('; ')
            } else if (typeof d.detail === 'string') {
              msg = d.detail
            } else if (typeof d.detail === 'object' && d.detail.msg) {
              msg = d.detail.msg
            }
          }
        } catch {}
        showError('Błąd', msg)
        return
      }

      // jeśli użytkownik dodał nowe pliki — wyślij
      if (filesToUpload.length && currentReportId) {
        const uploaded = await uploadImages(currentReportId, filesToUpload)
        if (uploaded) {
          setImages(prev => [...prev, ...uploaded])
          setFilesToUpload([])
        }
      }

      showSuccess('Zapisano', 'Raport zaktualizowany.')
      resetEphemeralState()
      onReportCreated()
      onClose()
    } catch {
      showError('Błąd połączenia', 'Nie można połączyć się z serwerem.')
    } finally {
      setSaving(false)
    }
  }

  // ============ UI ============

  if (!isOpen) return null

  const ClientSelect = (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">Klient *</label>
      {loadingClients ? (
        <div className="text-sm text-gray-600">Ładowanie…</div>
      ) : (
        <select
          value={form.client_id ?? ''}
          onChange={(e) => setForm({ ...form, client_id: e.target.value ? parseInt(e.target.value, 10) : null })}
          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">— wybierz klienta —</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      )}
    </div>
  )

  const TypeSelect = (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">Rodzaj pracy *</label>
      <div className={`${isMobile ? 'flex flex-col gap-2' : 'grid grid-cols-2 gap-2'}`}>
        <button
          type="button"
          onClick={() => setForm({ ...form, work_type: 'konserwacja', reported_by: '', reporter_name: '', reporter_phone: '' })}
          className={`px-4 py-2 rounded-md border text-sm ${form.work_type === 'konserwacja' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'}`}
        >
          Konserwacja/planowana
        </button>
        <button
          type="button"
          onClick={() => setForm({ ...form, work_type: 'awaria' })}
          className={`px-4 py-2 rounded-md border text-sm ${form.work_type === 'awaria' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'}`}
        >
          Awaria
        </button>
      </div>
    </div>
  )

  const CategorySelect = (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">Kategoria raportu *</label>
      <div className={`${isMobile ? 'flex flex-col gap-2' : 'grid grid-cols-2 gap-2'}`}>
        <button
          type="button"
          onClick={() => setForm({ ...form, category: 'water' })}
          className={`px-4 py-2 rounded-md border text-sm ${form.category === 'water' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'}`}
        >
          Wodociągi
        </button>
        <button
          type="button"
          onClick={() => setForm({ ...form, category: 'sewer' })}
          className={`px-4 py-2 rounded-md border text-sm ${form.category === 'sewer' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'}`}
        >
          Kanalizacja
        </button>
      </div>
    </div>
  )

  const ReporterSection = (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">Kto zgłosił?</label>
      <select
        value={form.reported_by}
        onChange={(e) => setForm({ ...form, reported_by: e.target.value as ReportedBy, reporter_name: '', reporter_phone: '' })}
        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <option value="">— wybierz —</option>
        <option value="system">System</option>
        <option value="osoba">Osoba fizyczna / Referat</option>
      </select>

      {form.reported_by === 'osoba' && (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
          <input
            type="text"
            placeholder="Imię i nazwisko"
            value={form.reporter_name}
            onChange={(e) => setForm({ ...form, reporter_name: e.target.value })}
            className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <input
            type="tel"
            placeholder="Telefon"
            value={form.reporter_phone}
            onChange={(e) => setForm({ ...form, reporter_phone: e.target.value })}
            className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      )}
    </div>
  )

  const DescriptionSection = (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">Opis prac</label>
      <textarea
        rows={5}
        value={form.description}
        onChange={(e) => setForm({ ...form, description: e.target.value })}
        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        placeholder="Opisz wykonane/planowane prace…"
      />
    </div>
  )

  const FinishedSection = (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">Praca zakończona?</label>
      <select
        value={form.is_finished ? 'yes' : 'no'}
        onChange={(e) => setForm({ ...form, is_finished: e.target.value === 'yes' })}
        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <option value="no">Nie</option>
        <option value="yes">Tak</option>
      </select>
    </div>
  )

  const MaterialsSection = (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">Materiały / usługi (jeśli były potrzebne)</label>
      <textarea
        rows={4}
        value={form.materials}
        onChange={(e) => setForm({ ...form, materials: e.target.value })}
        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        placeholder="Wpisz zużyte materiały, usługi, itp."
      />
    </div>
  )

  const RecommendationsSection = (
    <div className="mb-2">
      <label className="block text-sm font-medium text-gray-700 mb-2">Zalecenia (opcjonalnie, do 5000 znaków)</label>
      <textarea
        rows={4}
        maxLength={5000}
        value={form.recommendations}
        onChange={(e) => setForm({ ...form, recommendations: e.target.value })}
        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        placeholder="Zalecenia dla klienta / następnych działań…"
      />
      <div className={`text-right text-xs ${form.recommendations.length >= 4800 ? 'text-orange-600 font-medium' : 'text-gray-500'}`}>{form.recommendations.length}/5000</div>
    </div>
  )

  const InfrastructureSection = (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">Dotyczy obiektu infrastruktury?</label>
      <div className={`${isMobile ? 'flex flex-col gap-2' : 'grid grid-cols-2 gap-2'}`}>
        <button
          type="button"
          onClick={() => {
            setForm({ 
              ...form, 
              relates_to_infrastructure: false, 
              infrastructure_object_id: null,
              location: '',
              street_address: '',
              latitude: '',
              longitude: ''
            })
            setInfrastructureObjects([])
          }}
          className={`px-4 py-2 rounded-md border text-sm ${!form.relates_to_infrastructure ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'}`}
        >
          Nie
        </button>
        <button
          type="button"
          onClick={() => {
            setForm({ 
              ...form, 
              relates_to_infrastructure: true, 
              infrastructure_object_id: null,
              location: '',
              street_address: '',
              latitude: '',
              longitude: ''
            })
            if (form.client_id) {
              loadInfrastructureObjects(form.client_id)
            }
          }}
          className={`px-4 py-2 rounded-md border text-sm ${form.relates_to_infrastructure ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'}`}
        >
          Tak
        </button>
      </div>

      {form.relates_to_infrastructure && (
        <div className="mt-4">
          {loadingInfrastructure ? (
            <div className="text-sm text-gray-600">Ładowanie obiektów infrastruktury...</div>
          ) : infrastructureObjects.length === 0 ? (
            <div className="text-sm text-gray-600">Brak obiektów infrastruktury dla wybranego klienta.</div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Wybierz obiekt infrastruktury *</label>
              <select
                value={form.infrastructure_object_id ?? ''}
                onChange={(e) => {
                  const selectedId = e.target.value ? parseInt(e.target.value, 10) : null
                  const selectedObj = infrastructureObjects.find(obj => obj.id === selectedId)
                  if (selectedObj) {
                    setForm({
                      ...form,
                      infrastructure_object_id: selectedId,
                      location: selectedObj.location || '',
                      street_address: selectedObj.street_address || '',
                      latitude: selectedObj.latitude || '',
                      longitude: selectedObj.longitude || ''
                    })
                  } else {
                    setForm({
                      ...form,
                      infrastructure_object_id: null,
                      location: '',
                      street_address: '',
                      latitude: '',
                      longitude: ''
                    })
                  }
                }}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">— wybierz obiekt —</option>
                {infrastructureObjects.map(obj => (
                  <option key={obj.id} value={obj.id}>
                    {obj.name} - {obj.location} {obj.street_address}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}
    </div>
  )

  const ImagesSection = (
    <div className="mb-2">
      <label className="block text-sm font-medium text-gray-700 mb-2">Czy chcesz dodać zdjęcia?</label>
      <select
        value={wantImages}
        onChange={(e) => setWantImages(e.target.value as ('yes' | 'no'))}
        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <option value="no">Nie</option>
        <option value="yes">Tak</option>
      </select>

      {wantImages === 'yes' && (
        <div className="mt-4">
          <input
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp,image/heic"
            onChange={(e) => handleFilesSelected(e.target.files)}
            className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
          {filesToUpload.length > 0 && (
            <div className="mt-2 text-sm text-gray-600">
              Wybrano plików: {filesToUpload.length}
              <ul className="list-disc pl-5 mt-1 max-h-28 overflow-y-auto">
                {filesToUpload.map((f, i) => <li key={i}>{f.name}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  )

  // ----- Tryb EDYCJI: formularz zbiorczy + sekcja zdjęć -----
  if (isEditMode) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-stretch justify-center z-50 sm:items-center sm:p-4">
        <div className="bg-white sm:rounded-lg shadow-xl w-full sm:max-w-3xl overflow-hidden flex flex-col max-h-full" style={{ maxHeight: modalHeight }}>
          <div className="flex items-center justify-between p-4 sm:p-6 border-b shrink-0">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Edycja raportu {currentReportId}</h2>
            <button onClick={handleClose} className="p-1 -mr-1 text-gray-400 hover:text-gray-600" aria-label="Zamknij">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <div className="flex-1 min-h-0 p-4 sm:p-6 space-y-4 overflow-y-auto">
            {ClientSelect}
            {TypeSelect}
            {CategorySelect}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Adres interwencji</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Miejscowość</label>
                  <input
                    type="text"
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="np. Warszawa"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ulica/nr działki {(form.latitude && form.longitude && form.latitude.trim() && form.longitude.trim()) || form.relates_to_infrastructure ? '' : '*'}
                  </label>
                  <input
                    type="text"
                    value={form.street_address}
                    onChange={(e) => setForm({ ...form, street_address: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="np. ul. Przykładowa 123"
                    required={!(form.latitude && form.longitude && form.latitude.trim() && form.longitude.trim()) && !form.gpsSelected && !form.relates_to_infrastructure}
                  />
                </div>
                <div className="text-sm text-gray-600">
                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      onClick={async () => {
                        if (!navigator.geolocation) {
                          showError('Błąd', 'Twoja przeglądarka nie obsługuje geolokalizacji.')
                          return
                        }
                        try {
                          const position = await new Promise<GeolocationPosition>((resolve, reject) => {
                            navigator.geolocation.getCurrentPosition(resolve, reject, {
                              enableHighAccuracy: true,
                              timeout: 10000,
                              maximumAge: 300000
                            })
                          })
                          setForm({
                            ...form,
                            latitude: position.coords.latitude.toFixed(6),
                            longitude: position.coords.longitude.toFixed(6)
                          })
                          showSuccess('Sukces', 'Pobrano współrzędne GPS. Lokalizacja została zapisana.')
                        } catch (error) {
                          showError('Błąd', 'Nie można pobrać lokalizacji. Sprawdź uprawnienia przeglądarki.')
                        }
                      }}
                      className="px-3 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                    >
                      📍 Pobierz GPS
                    </button>
                    {form.latitude && form.longitude && form.latitude.trim() && form.longitude.trim() && (
                      <span className="text-green-600 font-medium">✓ Lokalizacja GPS pobrana</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {form.work_type === 'awaria' && ReporterSection}
            {DescriptionSection}
            {FinishedSection}
            {MaterialsSection}
            {RecommendationsSection}

            {/* Sekcja obrazów (lista + upload) */}
            <div className="mt-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Załączone zdjęcia</h3>
              {images.length === 0 ? (
                <div className="text-sm text-gray-600">Brak zdjęć.</div>
              ) : (
                <ul className="space-y-2">
                  {images.map(img => (
                    <li key={img.id} className="flex items-center justify-between border rounded-md p-2">
                      <div className="text-sm">
                        <div className="font-medium">{img.original_filename}</div>
                        <div className="text-gray-500">{img.file_path}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <a
                          href={`/${img.file_path}`}
                          target="_blank"
                          rel="noreferrer"
                          className="px-3 py-1 text-sm text-blue-700 bg-blue-50 rounded hover:bg-blue-100"
                        >
                          Podgląd
                        </a>
                        <button
                          onClick={() => deleteImage(img.id)}
                          className="px-3 py-1 text-sm text-red-700 bg-red-50 rounded hover:bg-red-100"
                        >
                          Usuń
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">Dodaj kolejne zdjęcia</label>
                <input
                  type="file"
                  multiple
                  accept="image/jpeg,image/png,image/webp,image/heic"
                  onChange={(e) => handleFilesSelected(e.target.files)}
                  className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
                {filesToUpload.length > 0 && (
                  <div className="mt-1 text-sm text-gray-600">Wybrano: {filesToUpload.length} plików</div>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 p-4 sm:p-6 border-t shrink-0">
            <button
              onClick={handleClose}
              className="min-h-[44px] px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
            >
              Anuluj
            </button>
            <button
              onClick={submitEdit}
              disabled={saving || loadingClients}
              className="min-h-[44px] px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
            >
              {saving ? 'Zapisywanie…' : 'Zapisz zmiany'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Sekcja adresowa
  const AddressSection = (
    <div className="mb-4 space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Miejscowość</label>
        <input
          type="text"
          value={form.location}
          onChange={(e) => setForm({ ...form, location: e.target.value })}
          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="np. Warszawa"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Ulica/nr działki {(form.latitude && form.longitude && form.latitude.trim() && form.longitude.trim()) || form.gpsSelected || form.relates_to_infrastructure ? '' : '*'}
        </label>
        <input
          type="text"
          value={form.street_address}
          onChange={(e) => setForm({ ...form, street_address: e.target.value })}
          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="np. ul. Przykładowa 123"
          required={!(form.latitude && form.longitude && form.latitude.trim() && form.longitude.trim()) && !form.relates_to_infrastructure}
        />
      </div>
      <div className="text-sm text-gray-600">
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={async () => {
              if (!navigator.geolocation) {
                showError('Błąd', 'Twoja przeglądarka nie obsługuje geolokalizacji.')
                return
              }
              setForm({ ...form, gpsSelected: true })
              try {
                const position = await new Promise<GeolocationPosition>((resolve, reject) => {
                  navigator.geolocation.getCurrentPosition(resolve, reject, {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 300000
                  })
                })
                setForm({
                  ...form,
                  latitude: position.coords.latitude.toFixed(6),
                  longitude: position.coords.longitude.toFixed(6)
                })
                showSuccess('Sukces', 'Pobrano współrzędne GPS. Lokalizacja została zapisana.')
              } catch (error) {
                showError('Błąd', 'Nie można pobrać lokalizacji. Sprawdź uprawnienia przeglądarki.')
              }
            }}
            className="px-3 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700"
          >
            📍 Pobierz GPS
          </button>
          {form.latitude && form.longitude && form.latitude.trim() && form.longitude.trim() && (
            <span className="text-green-600 font-medium">✓ Lokalizacja GPS pobrana</span>
          )}
        </div>
      </div>
    </div>
  )

  // ----- Tryb TWORZENIA: modal krokowy -----
  const renderStep = () => {
    switch (step) {
      case 1: return (<>{ClientSelect}</>)
      case 2: return (<>{TypeSelect}</>)
      case 3: return (<>{CategorySelect}</>)
      case 4: return (<>{InfrastructureSection}</>)
      case 5: return !form.relates_to_infrastructure ? (<>{AddressSection}</>) : (form.work_type === 'awaria' ? (<>{ReporterSection}</>) : (<>{DescriptionSection}</>))
      case 6: return !form.relates_to_infrastructure ? (form.work_type === 'awaria' ? (<>{ReporterSection}</>) : (<>{DescriptionSection}</>)) : (form.work_type === 'awaria' ? (<>{DescriptionSection}</>) : (<>{FinishedSection}</>))
      case 7: return !form.relates_to_infrastructure ? (form.work_type === 'awaria' ? (<>{DescriptionSection}</>) : (<>{FinishedSection}</>)) : (form.work_type === 'awaria' ? (<>{FinishedSection}</>) : (<>{MaterialsSection}</>))
      case 8: return !form.relates_to_infrastructure ? (form.work_type === 'awaria' ? (<>{FinishedSection}</>) : (<>{MaterialsSection}</>)) : (form.work_type === 'awaria' ? (<>{MaterialsSection}</>) : (<>{RecommendationsSection}</>))
      case 9: return !form.relates_to_infrastructure ? (form.work_type === 'awaria' ? (<>{MaterialsSection}</>) : (<>{RecommendationsSection}</>)) : (form.work_type === 'awaria' ? (<>{RecommendationsSection}</>) : (<>{ImagesSection}</>))
      case 10: return !form.relates_to_infrastructure ? (form.work_type === 'awaria' ? (<>{RecommendationsSection}</>) : (<>{ImagesSection}</>)) : (form.work_type === 'awaria' ? (<>{ImagesSection}</>) : null)
      case 11: return !form.relates_to_infrastructure ? (form.work_type === 'awaria' ? (<>{ImagesSection}</>) : null) : null
      default: return null
    }
  }

  const canGoNext = (): boolean => {
    if (step === 1) return Boolean(form.client_id)
    if (step === 2) return form.work_type === 'konserwacja' || form.work_type === 'awaria'
    if (step === 3) return form.category === 'water' || form.category === 'sewer'
    if (step === 4) {
      // Jeśli wybrano infrastrukturę, musi być wybrany obiekt
      if (form.relates_to_infrastructure) {
        return Boolean(form.infrastructure_object_id)
      }
      return true // Jeśli nie dotyczy infrastruktury, można przejść dalej
    }

    // Walidacja dla kroku adresu (tylko jeśli nie wybrano infrastruktury)
    const addressStep = 5 // krok adresu gdy nie wybrano infrastruktury
    if (step === addressStep && !form.relates_to_infrastructure) {
      // Sprawdź czy adres jest wymagany - tylko jeśli nie zostały podane współrzędne GPS i nie wybrano GPS
      const hasGPS = form.latitude && form.longitude && form.latitude.trim() && form.longitude.trim()
      if (!hasGPS && !form.gpsSelected) {
        // Jeśli nie ma GPS i nie wybrano GPS, sprawdź adres
        if (!form.street_address || !form.street_address.trim()) {
          return false // Blokuj przejście dalej jeśli nie ma adresu, GPS i nie wybrano GPS
        }
      }
      return true // Można przejść dalej jeśli jest GPS, wybrano GPS lub adres
    }

    // Walidacja dla kroku zgłaszającego awarię (różne pozycje w zależności od wyboru infrastruktury)
    const reporterStepForInfra = 5 // gdy wybrano infrastrukturę
    const reporterStepForManual = 6 // gdy nie wybrano infrastruktury
    const currentReporterStep = form.relates_to_infrastructure ? reporterStepForInfra : reporterStepForManual

    if (step === currentReporterStep && form.work_type === 'awaria') {
      return form.reported_by === 'system' || form.reported_by === 'osoba'
    }
    return true
  }

  const getTotalSteps = (): number => {
    const baseSteps = form.relates_to_infrastructure ? 9 : 10 // Jeśli infrastruktura, pomijamy krok adresu
    return form.work_type === 'awaria' ? baseSteps + 1 : baseSteps
  }

  const next = () => setStep(Math.min(step + 1, getTotalSteps()))
  const prev = () => setStep(Math.max(1, step - 1))

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-stretch justify-center z-50 sm:items-center sm:p-4">
      <div className="bg-white sm:rounded-lg shadow-xl w-full sm:max-w-xl overflow-hidden flex flex-col max-h-full" style={{ maxHeight: modalHeight }}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b shrink-0">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Nowy raport serwisowy</h2>
          <button onClick={handleClose} className="p-1 -mr-1 text-gray-400 hover:text-gray-600" aria-label="Zamknij">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        {/* Pasek postępu kreatora */}
        <div className="h-1.5 bg-gray-100 shrink-0">
          <div
            className="h-full bg-blue-600 rounded-r-full transition-all duration-300"
            style={{ width: `${Math.round((step / getTotalSteps()) * 100)}%` }}
          />
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 p-4 sm:p-6 overflow-y-auto">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm text-gray-500">Krok {step} z {getTotalSteps()}</div>
            <div className="text-xs text-gray-400">{Math.round((step / getTotalSteps()) * 100)}%</div>
          </div>
          {renderStep()}
          {!canGoNext() && step < getTotalSteps() && (
            <div className="mt-3 flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              <span className="shrink-0">ℹ️</span>
              <span>Uzupełnij wymagane pola powyżej, aby przejść dalej.</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`shrink-0 ${isMobile ? 'flex flex-col gap-3 p-4' : 'flex items-center justify-between gap-2 p-6'} border-t`}>
          <div className={`${isMobile ? 'flex flex-col gap-2' : 'flex items-center gap-2'}`}>
            <button
              type="button"
              onClick={handleSaveDraftClick}
              className={`${isMobile ? 'min-h-[44px] px-3 py-2 text-sm' : 'px-3 py-2 text-sm'} font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md`}
            >
              Zapisz wersję roboczą
            </button>
            <button
              type="button"
              onClick={clearDraft}
              className={`${isMobile ? 'min-h-[44px] px-3 py-2 text-sm' : 'px-3 py-2 text-sm'} font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md`}
            >
              Wyczyść szkic
            </button>
          </div>

          <div className={`${isMobile ? 'flex justify-between gap-2' : 'flex items-center gap-2'}`}>
            <button
              onClick={prev}
              disabled={step === 1}
              className={`min-h-[44px] ${isMobile ? 'flex-1 px-3 py-2 text-sm' : 'px-4 py-2 text-sm'} font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50`}
            >
              Wstecz
            </button>
            {step < getTotalSteps() ? (
              <button
                onClick={next}
                disabled={!canGoNext()}
                className={`min-h-[44px] ${isMobile ? 'flex-1 px-3 py-2 text-sm' : 'px-4 py-2 text-sm'} font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50`}
              >
                Dalej
              </button>
            ) : (
              <button
                onClick={submitCreate}
                disabled={saving || loadingClients}
                className={`min-h-[44px] ${isMobile ? 'flex-1 px-3 py-2 text-sm' : 'px-4 py-2 text-sm'} font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50`}
              >
                {saving ? 'Zapisywanie…' : 'Zapisz raport'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function nullIfEmpty(value: string | null | undefined): string | null {
  if (value === undefined || value === null) return null
  const t = String(value).trim()
  return t.length === 0 ? null : t
}
